import React, { useEffect, useState, useCallback, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useDispatch, useSelector } from "react-redux";
import { listStoreBanners } from "../../Redux/Actions/BannerActions";
import "../styles/hero-embla.css";

const useDotButton = (emblaApi) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onDotButtonClick = useCallback(
    (index) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApiInstance) => {
    setScrollSnaps(emblaApiInstance.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApiInstance) => {
    setSelectedIndex(emblaApiInstance.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);

    emblaApi.on("reInit", onInit);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

const DotButton = ({ className, onClick }) => (
  <button type="button" className={className} onClick={onClick} />
);

const HeroCarousel = () => {
  const dispatch = useDispatch();

  const bannerStoreList = useSelector((state) => state.bannerStoreList);
  const { loading, error, banners: rawBanners } = bannerStoreList || {};

  const banners = useMemo(() => {
    if (Array.isArray(rawBanners)) return rawBanners;
    if (Array.isArray(rawBanners?.banners)) return rawBanners.banners;
    return [];
  }, [rawBanners]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  useEffect(() => {
    if (!banners.length) {
      dispatch(listStoreBanners());
    }
  }, [dispatch, banners.length]);

  useEffect(() => {
    if (!emblaApi || !banners.length) return;

    const id = setInterval(() => {
      emblaApi.scrollNext();
    }, 5000);

    return () => clearInterval(id);
  }, [emblaApi, banners.length]);

  const dots = Array.isArray(scrollSnaps) ? scrollSnaps : [];
  const hasBanners = banners.length > 0;

  if (!loading && !hasBanners) {
    return null;
  }

  return (
    <div className="hero-embla">
      <div className="embla">
        {hasBanners && (
          <>
            <div className="embla__viewport" ref={emblaRef}>
              <div className="embla__container">
                {banners.map((b) => (
                  <div className="embla__slide" key={b._id}>
                    <div
                      className="embla__slide__background"
                      style={{ backgroundImage: `url(${b.image})` }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {!loading && !error && banners.length > 1 && (
              <div className="embla__controls embla__controls--dots-only">
                <div className="embla__dots">
                  {dots.map((_, index) => (
                    <DotButton
                      key={index}
                      onClick={() => onDotButtonClick(index)}
                      className={
                        "embla__dot" +
                        (index === selectedIndex ? " embla__dot--selected" : "")
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HeroCarousel;